import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import TCBadge from "@/components/TCBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Landmark, TrendingUp, ShoppingBag, RotateCw, CalendarClock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { API_URL as API } from "@/lib/api";
const DOUGHNUT_COLORS = ['#0284c7', '#0d9488', '#d97706', '#7c3aed', '#e11d48', '#64748b'];
const OTROS_COLORS = ['#6366f1', '#0891b2', '#ca8a04', '#be185d', '#059669', '#dc2626', '#7c3aed', '#0284c7', '#d97706', '#64748b', '#0d9488', '#e11d48'];

const FamiliaSelect = ({ value, onChange, familias, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-7 w-40 text-xs" data-testid={testId}>
      <SelectValue placeholder="General" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">General</SelectItem>
      {familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
    </SelectContent>
  </Select>
);

const DashboardEstrategico = ({ globalTC }) => {
  const [products, setProducts] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipoCambio, setTipoCambio] = useState(9.4);

  useEffect(() => {
    if (globalTC !== null && globalTC !== undefined) setTipoCambio(globalTC);
  }, [globalTC]);
  const [famKpis, setFamKpis] = useState("__all__");
  const [famTop, setFamTop] = useState("__all__");
  const [famDoughnut, setFamDoughnut] = useState("__all__");
  const [famVentas, setFamVentas] = useState("__all__");
  const [showOtrosDetail, setShowOtrosDetail] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, eRes, fRes] = await Promise.all([
        axios.get(`${API}/api/products`),
        axios.get(`${API}/api/entregas/all`),
        axios.get(`${API}/api/filters`),
      ]);
      setProducts(pRes.data);
      setEntregas(eRes.data.entregas || []);
      setFamilias(fRes.data.familias || []);
    } catch {
      toast.error("Error al cargar dashboard estrategico");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ff = (arr, fam) => fam === "__all__" ? arr : arr.filter(x => x.familia === fam);
  const tc = tipoCambio || 1;
  const formatBs = (v) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Bs';
  const formatUSD = (v) => '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v / tc);

  // KPIs
  const kpis = useMemo(() => {
    const p = ff(products, famKpis);
    const e = ff(entregas, famKpis);
    const capital_pmenor = p.reduce((s, x) => s + (x.stock || 0) * (x.pmenor || 0), 0);
    const capital_pmayor = p.reduce((s, x) => s + (x.stock || 0) * (x.pmayor || 0), 0);
    const valor_ventas_pmayor = e.reduce((s, x) => s + (x.cantidad || 0) * (x.pmayor || 0), 0);
    const valor_ventas_pmenor = e.reduce((s, x) => s + (x.cantidad || 0) * (x.pmenor || 0), 0);
    const total_salida = p.reduce((s, x) => s + (x.salida || 0), 0);
    const total_stock = p.reduce((s, x) => s + (x.stock || 0), 0);
    const tasa_rotacion = total_stock > 0 ? Math.round(total_salida / total_stock * 1000) / 10 : 0;
    const fechas = [...new Set(e.map(x => x.fecha).filter(Boolean))];
    const total_unidades_e = e.reduce((s, x) => s + (x.cantidad || 0), 0);
    const salida_diaria = fechas.length > 0 ? total_unidades_e / fechas.length : 0;
    const dias_cobertura = salida_diaria > 0 ? Math.round(total_stock / salida_diaria) : 999;
    return { capital_pmenor, capital_pmayor, valor_ventas_pmayor, valor_ventas_pmenor, tasa_rotacion, dias_cobertura, total_skus: p.length };
  }, [products, entregas, famKpis]);

  // Top 10 Productos Estrella
  const topProductos = useMemo(() => {
    const e = ff(entregas, famTop);
    const map = {};
    e.forEach(x => {
      if (!map[x.codigo]) map[x.codigo] = { codigo: x.codigo, descripcion: x.descripcion, familia: x.familia, valor: 0 };
      map[x.codigo].valor += (x.cantidad || 0) * (x.pmayor || 0);
    });
    return Object.values(map).sort((a, b) => b.valor - a.valor).slice(0, 10);
  }, [entregas, famTop]);

  // Composición Capital (Doughnut)
  const { composicion, otrosDetail } = useMemo(() => {
    const p = ff(products, famDoughnut);
    const map = {};
    p.forEach(x => {
      const key = famDoughnut !== "__all__" ? (x.subfamilia || "Sin SubFamilia") : (x.familia || "Sin Familia");
      map[key] = (map[key] || 0) + (x.stock || 0) * (x.pmenor || 0);
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    const result = [];
    const detail = [];
    sorted.forEach(([name, capital], i) => {
      if (i < 5) result.push({ familia: name, capital });
      else detail.push({ familia: name, capital });
    });
    const otrosTotal = detail.reduce((s, x) => s + x.capital, 0);
    if (otrosTotal > 0) result.push({ familia: "Otros", capital: otrosTotal });
    return { composicion: result, otrosDetail: detail };
  }, [products, famDoughnut]);

  // Ventas por Mes
  const ventasPorMes = useMemo(() => {
    const e = ff(entregas, famVentas);
    const map = {};
    e.forEach(x => {
      if (!x.fecha) return;
      const mes = x.fecha.substring(0, 7);
      if (!map[mes]) map[mes] = { mes, valor: 0, unidades: 0 };
      map[mes].valor += (x.cantidad || 0) * (x.pmayor || 0);
      map[mes].unidades += x.cantidad || 0;
    });
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes));
  }, [entregas, famVentas]);

  if (loading) {
    return <Card className="p-8 text-center border border-border"><p className="text-muted-foreground">Cargando Dashboard Estrategico...</p></Card>;
  }

  const kpiCards = [
    { title: "Capital Inmovilizado", value: formatBs(kpis.capital_pmenor), usd: formatUSD(kpis.capital_pmenor), subtitle: `${kpis.total_skus} SKUs`, icon: Landmark, color: "text-[#FF9900]", bg: "bg-[#FF9900]/10", border: "border-[#FF9900]/30" },
    { title: "Valor Ventas (PMayor)", value: formatBs(kpis.valor_ventas_pmayor), usd: formatUSD(kpis.valor_ventas_pmayor), subtitle: "Generado por salidas", icon: ShoppingBag, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { title: "Valor Entradas (PMenor)", value: formatBs(kpis.valor_ventas_pmenor), usd: formatUSD(kpis.valor_ventas_pmenor), subtitle: "Invertido en compras", icon: TrendingUp, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
    { title: "Tasa de Rotacion", value: `${kpis.tasa_rotacion}%`, subtitle: "Velocidad renovacion", icon: RotateCw, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
    { title: "Dias de Cobertura", value: kpis.dias_cobertura > 900 ? "N/A" : `${kpis.dias_cobertura} dias`, subtitle: "Stock estimado restante", icon: CalendarClock, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200" },
  ];

  return (
    <div className="space-y-5" data-testid="dashboard-estrategico">
      {/* TC Badge */}
      {globalTC !== null && globalTC !== undefined && (
        <div className="flex justify-end"><TCBadge tc={globalTC} /></div>
      )}

      {/* KPIs */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="heading-font text-sm font-semibold text-foreground">KPIs Estrategicos</h3>
          <FamiliaSelect value={famKpis} onChange={setFamKpis} familias={familias} testId="est-fam-kpis" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpiCards.map((k, i) => (
            <Card key={i} className={`p-4 border ${k.border} shadow-sm`} data-testid={`est-kpi-${i}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="body-font text-xs text-muted-foreground uppercase tracking-wide">{k.title}</p>
                  <p className="heading-font text-xl font-black text-foreground mt-1 truncate">{k.value}</p>
                  {k.usd && <p className="mono-font text-sm text-[#FF9900] mt-0.5">{k.usd}</p>}
                  <p className="body-font text-xs text-muted-foreground mt-0.5">{k.subtitle}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${k.bg} flex-shrink-0`}><k.icon className={`h-5 w-5 ${k.color}`} /></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top 10 Productos Estrella */}
        <Card className="border border-border shadow-sm" data-testid="est-top-productos">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <div>
              <h3 className="heading-font text-base font-semibold text-foreground">Top 10 Productos Estrella</h3>
              <p className="body-font text-xs text-muted-foreground mt-0.5">Mayor retorno financiero (Cant x PMayor)</p>
            </div>
            <FamiliaSelect value={famTop} onChange={setFamTop} familias={familias} testId="est-fam-top" />
          </div>
          <div className="p-4">
            {topProductos.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-8">Sin datos para esta familia</p>
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={topProductos} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }} barSize={24}>
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="descripcion" width={210}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const text = payload.value || '';
                      return (
                        <foreignObject x={x - 205} y={y - 18} width={200} height={36}>
                          <div xmlns="http://www.w3.org/1999/xhtml"
                            style={{ fontSize: '9px', lineHeight: '1.25', textAlign: 'right', color: '#334155', fontFamily: 'sans-serif', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {text}
                          </div>
                        </foreignObject>
                      );
                    }} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
                        <p className="font-semibold text-foreground mb-1">{d.descripcion}</p>
                        <p className="text-muted-foreground mono-font text-[10px]">{d.codigo}</p>
                        <p className="text-[#FF9900] font-bold mt-1">{formatBs(d.valor)} ({formatUSD(d.valor)})</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="valor" fill="#0284c7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Composición Capital */}
        <Card className="border border-border shadow-sm" data-testid="est-composicion">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <div>
              <h3 className="heading-font text-base font-semibold text-foreground">Composicion del Capital Inmovilizado</h3>
              <p className="body-font text-xs text-muted-foreground mt-0.5">
                {famDoughnut === "__all__" ? "Distribucion por Familia" : `SubFamilias de ${famDoughnut}`}
              </p>
            </div>
            <FamiliaSelect value={famDoughnut} onChange={(v) => { setFamDoughnut(v); setShowOtrosDetail(false); }} familias={familias} testId="est-fam-doughnut" />
          </div>
          <div className="p-4">
            {composicion.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-8">Sin datos</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={showOtrosDetail ? 240 : 320}>
                  <PieChart>
                    <Pie data={composicion} cx="50%" cy="50%" innerRadius={showOtrosDetail ? 50 : 70} outerRadius={showOtrosDetail ? 90 : 120}
                      dataKey="capital" nameKey="familia" paddingAngle={2}
                      label={({ familia, percent }) => `${familia} (${(percent * 100).toFixed(0)}%)`} labelLine={false}
                      onClick={(data) => {
                        if (data.familia === "Otros") setShowOtrosDetail(prev => !prev);
                      }}
                      style={{ cursor: 'pointer' }}>
                      {composicion.map((entry, i) => (
                        <Cell key={i} fill={DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]}
                          stroke={entry.familia === "Otros" && !showOtrosDetail ? "#334155" : "none"}
                          strokeWidth={entry.familia === "Otros" && !showOtrosDetail ? 2 : 0}
                          strokeDasharray={entry.familia === "Otros" && !showOtrosDetail ? "4 2" : "none"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => [`${formatBs(v)} (${formatUSD(v)})`, 'Capital']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>

                {otrosDetail.length > 0 && !showOtrosDetail && (
                  <p className="text-center text-[10px] text-muted-foreground -mt-1 cursor-pointer hover:text-[#FF9900] transition-colors"
                    onClick={() => setShowOtrosDetail(true)} data-testid="otros-hint">
                    Clic en "Otros" para ver el desglose
                  </p>
                )}

                {showOtrosDetail && otrosDetail.length > 0 && (
                  <div className="mt-2 border-t border-border pt-3" data-testid="otros-detail">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-xs font-semibold text-foreground">
                        Desglose de "Otros" ({otrosDetail.length} {famDoughnut === "__all__" ? "familias" : "subfamilias"})
                      </h4>
                      <button onClick={() => setShowOtrosDetail(false)}
                        className="text-[10px] text-muted-foreground hover:text-muted-foreground transition-colors" data-testid="otros-close">
                        Cerrar
                      </button>
                    </div>
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={otrosDetail} cx="50%" cy="50%" innerRadius={35} outerRadius={65}
                          dataKey="capital" nameKey="familia" paddingAngle={1}
                          label={({ familia, percent }) => percent > 0.04 ? `${familia} (${(percent * 100).toFixed(0)}%)` : ''}
                          labelLine={false}>
                          {otrosDetail.map((_, i) => <Cell key={i} fill={OTROS_COLORS[i % OTROS_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => [`${formatBs(v)} (${formatUSD(v)})`, 'Capital']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-1">
                      {otrosDetail.map((item, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: OTROS_COLORS[i % OTROS_COLORS.length] }} />
                          <span className="text-[9px] text-muted-foreground">{item.familia}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Ventas por Periodo */}
        <Card className="border border-border shadow-sm lg:col-span-2" data-testid="est-ventas-periodo">
          <div className="p-4 border-b border-border flex justify-between items-center">
            <div>
              <h3 className="heading-font text-base font-semibold text-foreground">Valor de Ventas por Periodo</h3>
              <p className="body-font text-xs text-muted-foreground mt-0.5">Ingresos generados (Cant x PMayor)</p>
            </div>
            <FamiliaSelect value={famVentas} onChange={setFamVentas} familias={familias} testId="est-fam-ventas" />
          </div>
          <div className="p-4">
            {ventasPorMes.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-8">Sin datos para esta familia</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={ventasPorMes}>
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`${formatBs(v)} (${formatUSD(v)})`, 'Valor']} />
                  <Bar dataKey="valor" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardEstrategico;
